
import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { isoToBritishInput, britishInputToIso, formatBritishTime } from '@/lib/date';
import {
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { createEventSchema } from '@tyto/shared';
import type { FC } from 'react';
import { toast } from 'sonner';
import { MarkdownEditor } from '@/components/ui/markdown-editor';

type EventFormValues = z.infer<typeof createEventSchema>;

interface OrgCreateEventProps {
  orgId: string
  eventCreated: (eventId: string) => void
  closeForm: () => void
}

export const CreateEventForm: FC<OrgCreateEventProps> = ({
  orgId,
  eventCreated,
  closeForm
}) => {
  const queryClient = useQueryClient();
  // Setup form for creating/editing events
  const form = useForm<EventFormValues>({
    resolver: zodResolver(createEventSchema),
    defaultValues: {
      name: '',
      description: '',
      startDate: new Date().toISOString()
    },
  });

  // Mutation for creating a new event
  const createEventMutation = useMutation({
    mutationFn: async (data: EventFormValues) => {
      const response = await fetch(`/api/org/${orgId}/event`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...data,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create event');
      }

      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['organizationEvents'] });
      form.reset();
      toast.success("Event created successfully");
      eventCreated(data.eventId);
    },
    onError: (error) => {
      console.error('Error creating event:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to create event');
    }
  });


  // Handle form submission for creating an event
  const onCreateSubmit = (data: EventFormValues) => {
    console.log('Data: ', data)
    createEventMutation.mutate(data);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onCreateSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Event Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter event name..." {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="startDate"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Start Date and Time</FormLabel>
              <FormControl>
                <Input
                  type="datetime-local"
                  value={isoToBritishInput(field.value)}
                  onChange={x => field.onChange(britishInputToIso(x.target.value))}
                />
              </FormControl>
              <div className="text-xs text-muted-foreground mt-1">
                {field.value && formatBritishTime(field.value)}
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <MarkdownEditor
                  placeholder="Enter event description..."
                  className="min-h-[120px]"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <DialogFooter className="flex justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={() => closeForm()}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={createEventMutation.isPending}
          >
            {createEventMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Event'
            )}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  )
}